import DevDocsShell from "@/components/DevDocsShell";
import { Link } from "react-router-dom";
import { Hash, FileText, ChevronLeft } from "lucide-react";

/**
 * Plain-English dictionary of every field in the enriched-lead response —
 * what it means, how it's derived, what range/enum values it takes, and when
 * it's null. The thing a buyer reads to decide if the data is worth paying for.
 */
const FIELDS = [
  {
    group: "company_overview",
    description: "Firmographics summary of the account.",
    items: [
      {
        key: "company_overview.company_size",
        type: "string",
        desc: "Bucketed headcount estimate. Derived from firmographic signals (job-board activity, LinkedIn, press). One of: '1-10', '11-50', '51-200', '201-1000', '1000+', or 'Unknown' if we can't form a confident guess.",
        example: '"51-200"',
        when_null: "No reliable headcount signal was found.",
      },
      {
        key: "company_overview.industry",
        type: "string",
        desc: "Primary industry classification in plain English. Not a GICS code — this is written for human readers.",
        example: '"B2B SaaS"',
        when_null: "Industry couldn't be inferred (very rare).",
      },
      {
        key: "company_overview.summary",
        type: "string",
        desc: "One-paragraph description of what the company does, written for a salesperson about to send outreach. Reflects positioning and product focus, not just scraped marketing copy.",
        example: '"Acme Corp builds workflow automation for mid-market finance teams, focused on month-end close acceleration."',
        when_null: "Only on complete enrichment failure (score_rationale will explain).",
      },
    ],
  },
  {
    group: "tech_stack",
    description: "Technologies the account uses, grouped by category. Lists are deduped and ordered by signal strength.",
    items: [
      { key: "tech_stack.cloud", type: "string[]", desc: "Cloud providers detected (AWS, GCP, Azure, Cloudflare, etc.).", example: '["AWS", "Cloudflare"]', when_null: "Returns [] (empty), never null." },
      { key: "tech_stack.crm", type: "string[]", desc: "CRM in use (HubSpot, Salesforce, Pipedrive, Attio, Close, …).", example: '["HubSpot"]', when_null: "Returns [] when no CRM signal was found." },
      { key: "tech_stack.security", type: "string[]", desc: "Security / IAM tooling (Okta, 1Password, CrowdStrike, …).", example: '["Okta"]', when_null: "Returns [] when nothing matches." },
      { key: "tech_stack.infrastructure", type: "string[]", desc: "Infra and devtools (Kubernetes, Datadog, Snowflake, …).", example: '["Datadog", "Snowflake"]', when_null: "Returns [] when nothing matches." },
      { key: "tech_stack.other", type: "string[]", desc: "Notable tools that didn't fit the categories above (Notion, Linear, Figma, Segment, …).", example: '["Notion", "Segment"]', when_null: "Returns [] when nothing matches." },
    ],
  },
  {
    group: "products",
    description: "Products or services the company sells. Order reflects prominence on their site.",
    items: [
      {
        key: "products",
        type: "string[]",
        desc: "Each entry is a one-line product name (and sometimes a short tag). Used to tailor outreach to the right line of business for multi-product accounts.",
        example: '["Workflow Builder", "Close Accelerator"]',
        when_null: "Returns [] for single-product companies where no distinct SKUs could be extracted.",
      },
    ],
  },
  {
    group: "intent_signals",
    description: "Fresh signals that the account might be in-market right now. We prefer recent signals (<90 days) over anything stale.",
    items: [
      { key: "intent_signals.hiring_signals", type: "string[]", desc: "Recent hires or open roles signalling intent. Each item is a short sentence (role + context). Pulled from ATS APIs and site crawls.", example: '["Hiring 3 Senior AEs in NYC (posted 12 days ago)"]', when_null: "Returns [] if no relevant roles are open." },
      { key: "intent_signals.recent_news", type: "string[]", desc: "Press hits from the last ~90 days. Deduped. Most-recent-first.", example: '["Series B $45M led by Accel (Feb 2026)"]', when_null: "Returns [] when the news search returned nothing." },
      { key: "intent_signals.content_published", type: "string[]", desc: "Articles, launches, or notable blog posts from the account.", example: '["Published \'The CFO guide to automated close\' on their blog"]', when_null: "Returns [] when no content was surfaced." },
      { key: "intent_signals.events_attended", type: "string[]", desc: "Industry events, sponsorships, or speaking slots.", example: '["Sponsoring SaaStr Annual 2026"]', when_null: "Returns [] when no event signal was found." },
    ],
  },
  {
    group: "buying_committee",
    description: "Who to talk to and in what order. Not a dump of the whole org chart — a curated short list.",
    items: [
      {
        key: "buying_committee",
        type: "object[]",
        desc: "Each item has `name`, `title`, `role` (e.g. 'Economic buyer', 'Champion', 'Technical evaluator'), and `linkedin_url` when available. Max ~5 people.",
        example: '[{"name":"Priya Rao","title":"VP Finance","role":"Economic buyer","linkedin_url":"https://linkedin.com/in/priyarao"}]',
        when_null: "Returns [] for very small companies where we couldn't identify distinct roles.",
      },
    ],
  },
  {
    group: "pain_points",
    description: "The problems this account most likely cares about right now. Based on tech stack gaps, industry pattern, and recent signals.",
    items: [
      {
        key: "pain_points",
        type: "string[]",
        desc: "Each item is one sentence describing a problem the account is likely feeling. Used to pick the right value prop angle.",
        example: '["Month-end close drags into week 2 because reconciliation is still manual."]',
        when_null: "Returns [] on sparse accounts where pain couldn't be inferred.",
      },
    ],
  },
  {
    group: "engagement_playbook",
    description: "AI-written outreach starter. Opinionated — pick the pieces that fit your voice.",
    items: [
      { key: "engagement_playbook.timing", type: "string", desc: "When to reach out. E.g. 'Within 2 weeks of their Series B announcement'.", example: '"Within 2 weeks — hiring signals suggest active evaluation."', when_null: "Rarely null; usually a hedged recommendation." },
      { key: "engagement_playbook.opening_line", type: "string", desc: "A personalized first-line for the outreach email. Not a template — references specifics from the enrichment.", example: '"Noticed Acme just closed a $45M Series B — scaling finance ops is going to look different in 60 days."', when_null: "Null on complete enrichment failure." },
      { key: "engagement_playbook.value_prop", type: "string", desc: "The positioning statement that's most relevant for this specific account.", example: '"We cut month-end close from 8 days to 2 for Series B+ fintechs."', when_null: "Null on complete enrichment failure." },
      { key: "engagement_playbook.case_study_angle", type: "string", desc: "Which proof story to lean on — references a persona and outcome, not a brand name.", example: '"Mid-market SaaS CFO who went from manual to automated close in a quarter."', when_null: "Null when no plausible analog pattern was identified." },
    ],
  },
  {
    group: "opportunity_score",
    description: "The single number sales teams filter by. 0 = ignore, 100 = highest priority.",
    items: [
      {
        key: "opportunity_score",
        type: "integer (0–100)",
        desc: "Composite score combining fit (firmographic match to your ICP) and intent (freshness and strength of in-market signals). Weighting is model-dependent; see score_rationale for the specific reasoning on each lead.",
        example: "72",
        when_null: "Never null — defaults to 0 on enrichment failure (with an explanation in score_rationale).",
      },
      {
        key: "score_rationale",
        type: "string",
        desc: "One-sentence plain-English explanation of why the score landed where it did. The thing you read when the score looks wrong.",
        example: '"Strong hiring + recent funding, but mid-market SaaS doesn\'t fit your enterprise-only ICP."',
        when_null: "Null only on complete enrichment failure.",
      },
    ],
  },
  {
    group: "tags",
    description: "Semantic labels for filtering and segmenting. Not a free-text free-for-all — bounded to a known vocabulary (persona, segment, intent-signal type).",
    items: [
      {
        key: "tags",
        type: "string[]",
        desc: "Mix of persona tags ('mid-market', 'cfo-led'), signal tags ('hiring-sales', 'recent-funding'), and intent tags ('actively-evaluating'). Use these to build saved views.",
        example: '["mid-market", "recent-funding", "actively-evaluating"]',
        when_null: "Returns [] when no tags could be inferred.",
      },
    ],
  },
];

export default function DevGlossary() {
  return (
    <DevDocsShell>
      <div data-testid="developers-glossary-page">
        <Link
          to="/developers"
          className="text-ink-500 hover:text-ink-900 text-xs uppercase tracking-widestx font-mono inline-flex items-center gap-1 mb-6"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Developer docs
        </Link>
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 flex items-center gap-1">
            <FileText className="w-3 h-3" /> Reference
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-ink-900 mt-2">
            Enriched-field glossary
          </h1>
          <p className="text-ink-500 text-base mt-3 max-w-2xl">
            What each key in the enriched-lead payload means, how it's derived, the shape it
            takes, and when it's empty or null. Written for a buyer deciding whether this data is
            good enough — not just a syntactic schema.
          </p>
        </div>

        {/* Floating TOC */}
        <div className="rounded-sm border border-ink-100 bg-ink-50 p-4 mb-10">
          <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-2">
            On this page
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
            {FIELDS.map((g) => (
              <a
                key={g.group}
                href={`#${g.group}`}
                className="font-mono text-ink-700 hover:text-brand-700"
              >
                {g.group}
              </a>
            ))}
          </div>
        </div>

        {FIELDS.map((group) => (
          <section
            key={group.group}
            id={group.group}
            className="mb-10 scroll-mt-20"
            data-testid={`glossary-group-${group.group}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <a
                href={`#${group.group}`}
                className="text-ink-300 hover:text-ink-700"
                aria-label="Anchor"
              >
                <Hash className="w-4 h-4" />
              </a>
              <h2 className="font-display text-xl font-bold text-ink-900 font-mono">
                {group.group}
              </h2>
            </div>
            <p className="text-sm text-ink-500 mb-4">{group.description}</p>
            <div className="space-y-4">
              {group.items.map((item) => (
                <div
                  key={item.key}
                  className="rounded-sm border border-ink-100 bg-ink-50 p-4"
                >
                  <div className="flex flex-wrap items-baseline gap-3">
                    <code className="font-mono text-sm text-ink-900 font-semibold">
                      {item.key}
                    </code>
                    <span className="text-xs text-brand-700 font-mono uppercase tracking-widestx">
                      {item.type}
                    </span>
                  </div>
                  <p className="text-sm text-ink-700 mt-2">{item.desc}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-1">
                        Example
                      </div>
                      <pre className="bg-ink-900 text-ink-50 rounded-sm px-2 py-1.5 text-[11px] font-mono overflow-x-auto">
                        {item.example}
                      </pre>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-1">
                        When null / empty
                      </div>
                      <p className="text-xs text-ink-600">{item.when_null}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </DevDocsShell>
  );
}
